import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import VippstarLogo from "../../assets/vippstar_logo.png";
import PatientSidebar from "../../components/PatientSidebar";
import { pathPatientActivityPlan, API_URL } from "../../config/constants";
import ImgMotor          from "../../assets/activity_types/motor.png";
import ImgSelfRegulatory from "../../assets/activity_types/self_regulatory.png";
import ImgCognitive      from "../../assets/activity_types/cognitive.png";
import ImgSensory        from "../../assets/activity_types/sensory.png";
import ImgInteractive    from "../../assets/activity_types/interactive.png";
import ImgTherapist      from "../../assets/therapist.png";
import ImgQuestionnaire  from "../../assets/questionnaire.png";
import {
  Layout, Main,
  TopBar, TopBarLogo, TopBarContent,
  SectionTitle, ProgressBarWrapper, ProgressBarTrack, ProgressBarFill, ProgressLabel,
  CardsRow, ActivityCard, CardIllustration, CardBody, CardTypeName, CardSubtext,
  CardButton,
  BottomRow, Panel, PanelWide, PanelTitle, PanelButton,
  TherapistAvatar, MessageText,
  WeeksRow, WeekItem, WeekLabel, WeekStats,
  QuestionnaireIcon, QuestionnaireTitle, QuestionnaireText,
} from "./styles";

const ACTIVITY_TYPES = [
  { id: 1, name: "Motor",           img: ImgMotor,          bg: "#edf6f9" },
  { id: 2, name: "Self-regulatory", img: ImgSelfRegulatory, bg: "#f0f7f4" },
  { id: 3, name: "Cognitive",       img: ImgCognitive,      bg: "#fdf6ec" },
  { id: 4, name: "Sensory",         img: ImgSensory,        bg: "#f0f9f5" },
  { id: 5, name: "Interactive",     img: ImgInteractive,    bg: "#fdf0f5" },
];

const ROADTRIP_WEEKS = [
  { week: 1, percent: 50,  activities: "4/8 Activities",  time: "28 min"     },
  { week: 2, percent: 125, activities: "10/8 Activities", time: "1 h 23 min" },
  { week: 3, percent: 25,  activities: "2/8 Activities",  time: "12 min"     },
  { week: 4, percent: 100, activities: "12/8 Activities", time: "1 h 06 min" },
  { week: 5, percent: 25,  activities: "2/8 Activities",  time: "12 min"     },
];

const R    = 34;
const CIRC = 2 * Math.PI * R;

const CircularProgress = ({ percent }) => {
  const capped   = Math.min(percent, 100);
  const offset   = CIRC * (1 - capped / 100);
  const achieved = percent >= 100;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88">
      <circle cx="44" cy="44" r={R} fill="none" stroke="#dde8f0" strokeWidth="7" />
      <circle
        cx="44" cy="44" r={R}
        fill="none"
        stroke={achieved ? "#1b3a6b" : "#26A9B6"}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={CIRC}
        strokeDashoffset={offset}
        transform="rotate(-90 44 44)"
      />
      <text x="44" y="49" textAnchor="middle" fontSize="13" fontWeight="700"
        fill={achieved ? "#1b3a6b" : "#333"}>
        {percent}%
      </text>
    </svg>
  );
};

const PatientHome = () => {
  const history      = useHistory();
  const token        = localStorage.getItem("token");

  const [countByType, setCountByType] = useState({});
  const [weeklyLogged, setWeeklyLogged] = useState(0);

  const weeklyGoal = Object.values(countByType).reduce((sum, n) => sum + n, 0);
  const weeklyPct  = weeklyGoal > 0 ? Math.round((weeklyLogged / weeklyGoal) * 100) : 0;

  useEffect(() => {
    fetch(`${API_URL}/me/activity-plan`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const counts = {};
        let logged = 0;
        data.forEach((a) => {
          counts[a.activity_type_id] = (counts[a.activity_type_id] || 0) + 1;
          logged += Number(a.log_count) || 0;
        });
        setCountByType(counts);
        setWeeklyLogged(logged);
      })
      .catch((err) => console.error("PatientHome activity-plan fetch error:", err));
  }, []); // eslint-disable-line

  return (
    <Layout>
      <TopBar>
        <TopBarLogo>
          <img src={VippstarLogo} alt="Vippstar" style={{ width: 80 }} />
        </TopBarLogo>
        <TopBarContent>
          <SectionTitle>Weekly Activities</SectionTitle>
          <ProgressBarWrapper>
            <ProgressBarTrack>
              <ProgressBarFill $pct={weeklyPct} />
            </ProgressBarTrack>
            <ProgressLabel>Goal: {weeklyLogged}/{weeklyGoal} Logged</ProgressLabel>
          </ProgressBarWrapper>
        </TopBarContent>
      </TopBar>

      <PatientSidebar activeNav="home" />

      <Main>
        <CardsRow>
          {ACTIVITY_TYPES.map((type) => (
            <ActivityCard key={type.id} onClick={() => history.push(pathPatientActivityPlan, { typeId: type.id })}>
              <CardIllustration $bg={type.bg}>
                <img src={type.img} alt={type.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: "12px", boxSizing: "border-box" }} />
              </CardIllustration>
              <CardBody>
                <CardTypeName>{type.name}</CardTypeName>
                <CardSubtext>{countByType[type.id] ?? 0} Activities</CardSubtext>

                <CardButton>{type.name} activities</CardButton>
              </CardBody>
            </ActivityCard>
          ))}
        </CardsRow>

        <BottomRow>
          <Panel>
            <PanelTitle>Therapist message</PanelTitle>
            <TherapistAvatar>
              <img src={ImgTherapist} alt="Therapist"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
            </TherapistAvatar>
            <MessageText>
              Hi… This week, we are working on the pincer grasp. Lorem ipsum dolor sit amet,
              consectetur adipiscing elit.
            </MessageText>
            <PanelButton>Caregiver support</PanelButton>
          </Panel>

          <PanelWide>
            <PanelTitle>Roadtrip</PanelTitle>
            <WeeksRow>
              {ROADTRIP_WEEKS.map(({ week, percent, activities, time }) => (
                <WeekItem key={week}>
                  <CircularProgress percent={percent} />
                  <WeekLabel>Week {week}</WeekLabel>
                  <WeekStats>{activities}<br />{time}</WeekStats>
                </WeekItem>
              ))}
            </WeeksRow>
            <PanelButton>Summary</PanelButton>
          </PanelWide>

          <Panel>
            <PanelTitle>Weekly Questionnaire</PanelTitle>
            <QuestionnaireIcon>
              <img src={ImgQuestionnaire} alt="Questionnaire" style={{ width: 72, objectFit: "contain" }} />
            </QuestionnaireIcon>
            <QuestionnaireTitle>Your questionnaire is ready!</QuestionnaireTitle>
            <QuestionnaireText>
              You have a pending form for this week. Please take a moment to complete it.
            </QuestionnaireText>
            <PanelButton>Fill out form</PanelButton>
          </Panel>
        </BottomRow>
      </Main>
    </Layout>
  );
};

export default PatientHome;
